import React, { useState } from 'react';
import {
  Calendar,
  Clock,
  FileText,
  Download,
  Eye,
  Printer,
  Mail,
  Filter,
  BarChart3,
  TrendingUp,
  UserX,
  Timer,
  Users,
  AlertCircle,
  X,
  ChevronDown,
  Loader2,
} from 'lucide-react';
import { DEPARTMENTS, formatDate } from './attendanceData';
import toast from 'react-hot-toast';

// Report configuration
const REPORT_TYPES = [
  {
    id: 'attendance-register',
    name: 'Monthly Attendance Register',
    description: 'Complete attendance record for the month with daily status',
    icon: Calendar,
    color: 'blue',
    format: ['PDF', 'Excel'],
  },
  {
    id: 'late-arrivals',
    name: 'Late Arrivals Report',
    description: 'Employees with late check-ins and frequency analysis',
    icon: AlertCircle,
    color: 'amber',
    format: ['PDF', 'Excel'],
  },
  {
    id: 'overtime',
    name: 'Overtime Report',
    description: 'Extra hours worked by employees with cost calculation',
    icon: Timer,
    color: 'violet',
    format: ['PDF', 'Excel'],
  },
  {
    id: 'absenteeism',
    name: 'Absenteeism Report',
    description: 'Absence patterns, trends and unplanned leaves',
    icon: UserX,
    color: 'red',
    format: ['PDF', 'Excel'],
  },
  {
    id: 'summary',
    name: 'Attendance Summary',
    description: 'Present/Absent/Leave counts and percentages',
    icon: BarChart3,
    color: 'emerald',
    format: ['PDF', 'Excel'],
  },
  {
    id: 'punctuality',
    name: 'Punctuality Report',
    description: 'On-time vs late arrival analysis by department',
    icon: TrendingUp,
    color: 'cyan',
    format: ['PDF', 'Excel'],
  },
  {
    id: 'department-wise',
    name: 'Department-wise Report',
    description: 'Attendance breakdown by department with comparisons',
    icon: Users,
    color: 'indigo',
    format: ['PDF', 'Excel'],
  },
  {
    id: 'individual',
    name: 'Individual Report',
    description: 'Detailed attendance record for a specific employee',
    icon: FileText,
    color: 'gray',
    format: ['PDF'],
  },
];

// Report Preview Modal
const ReportPreviewModal = ({ report, filters, onClose, onDownload, onEmail, onPrint }) => {
  const [loading, setLoading] = useState(true);

  // Simulate loading
  React.useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  if (!report) return null;

  const Icon = report.icon;
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    amber: 'bg-amber-100 text-amber-600',
    violet: 'bg-violet-100 text-violet-600',
    red: 'bg-red-100 text-red-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    cyan: 'bg-cyan-100 text-cyan-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    gray: 'bg-gray-100 text-gray-600',
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${colorClasses[report.color]} rounded-lg flex items-center justify-center`}>
              <Icon size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{report.name}</h3>
              <p className="text-xs text-gray-500">
                {formatDate(filters.startDate)} - {formatDate(filters.endDate)}
                {filters.department !== 'all' && ` • ${filters.department}`}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition">
            <X size={18} />
          </button>
        </div>

        {/* Report Preview Area */}
        <div className="flex-1 overflow-auto p-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <Loader2 size={40} className="text-blue-600 animate-spin mb-3" />
              <p className="text-gray-500">Generating report...</p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg">
              {/* Dummy Report Preview */}
              <div className="p-6 bg-gray-50 border-b border-gray-200">
                <div className="text-center mb-6">
                  <h2 className="text-xl font-bold text-gray-900">{report.name}</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Period: {formatDate(filters.startDate)} to {formatDate(filters.endDate)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Generated on {new Date().toLocaleString()}</p>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                    <p className="text-2xl font-bold text-emerald-600">89%</p>
                    <p className="text-xs text-gray-500">Attendance Rate</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                    <p className="text-2xl font-bold text-blue-600">234</p>
                    <p className="text-xs text-gray-500">Working Days</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                    <p className="text-2xl font-bold text-amber-600">18</p>
                    <p className="text-xs text-gray-500">Late Arrivals</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center border border-gray-200">
                    <p className="text-2xl font-bold text-gray-600">8.5h</p>
                    <p className="text-xs text-gray-500">Avg Hours/Day</p>
                  </div>
                </div>
              </div>

              {/* Dummy Table */}
              <div className="p-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-semibold text-gray-600">Employee</th>
                      <th className="text-center py-2 px-3 font-semibold text-gray-600">Present</th>
                      <th className="text-center py-2 px-3 font-semibold text-gray-600">Absent</th>
                      <th className="text-center py-2 px-3 font-semibold text-gray-600">Late</th>
                      <th className="text-center py-2 px-3 font-semibold text-gray-600">Leave</th>
                      <th className="text-center py-2 px-3 font-semibold text-gray-600">Attendance %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { name: 'Rahul Sharma', present: 22, absent: 1, late: 2, leave: 1, pct: 92 },
                      { name: 'Priya Patel', present: 24, absent: 0, late: 1, leave: 1, pct: 100 },
                      { name: 'Amit Kumar', present: 20, absent: 3, late: 3, leave: 0, pct: 87 },
                      { name: 'Sneha Reddy', present: 23, absent: 1, late: 1, leave: 1, pct: 96 },
                      { name: 'Vikram Singh', present: 21, absent: 2, late: 2, leave: 1, pct: 91 },
                    ].map((row, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 px-3 font-medium text-gray-900">{row.name}</td>
                        <td className="py-2 px-3 text-center text-emerald-600">{row.present}</td>
                        <td className="py-2 px-3 text-center text-red-600">{row.absent}</td>
                        <td className="py-2 px-3 text-center text-amber-600">{row.late}</td>
                        <td className="py-2 px-3 text-center text-blue-600">{row.leave}</td>
                        <td className="py-2 px-3 text-center">
                          <span className={`font-medium ${row.pct >= 90 ? 'text-emerald-600' : row.pct >= 80 ? 'text-amber-600' : 'text-red-600'}`}>
                            {row.pct}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-gray-400 text-center mt-4">
                  Showing preview data. Download the full report for complete details.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Actions Footer */}
        <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition text-sm font-medium"
          >
            Close
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={onPrint}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm font-medium"
            >
              <Printer size={14} />
              Print
            </button>
            <button
              onClick={() => onEmail(report)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm font-medium"
            >
              <Mail size={14} />
              Email
            </button>
            <div className="relative group">
              <button
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
              >
                <Download size={14} />
                Download
                <ChevronDown size={14} />
              </button>
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-32 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                {report.format.map(fmt => (
                  <button
                    key={fmt}
                    onClick={() => onDownload(fmt)}
                    className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Download {fmt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Report Card Component
const ReportCard = ({ report, onGenerate }) => {
  const Icon = report.icon;
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600 border-blue-200',
    amber: 'bg-amber-100 text-amber-600 border-amber-200',
    violet: 'bg-violet-100 text-violet-600 border-violet-200',
    red: 'bg-red-100 text-red-600 border-red-200',
    emerald: 'bg-emerald-100 text-emerald-600 border-emerald-200',
    cyan: 'bg-cyan-100 text-cyan-600 border-cyan-200',
    indigo: 'bg-indigo-100 text-indigo-600 border-indigo-200',
    gray: 'bg-gray-100 text-gray-600 border-gray-200',
  };

  return (
    <div
      onClick={() => onGenerate(report)}
      className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer hover:shadow-md hover:border-blue-300 transition group"
    >
      <div className={`w-10 h-10 ${colorClasses[report.color]} rounded-lg flex items-center justify-center mb-3`}>
        <Icon size={20} />
      </div>
      <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition">{report.name}</h3>
      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{report.description}</p>
      <div className="mt-3 flex items-center gap-2">
        {report.format.map(fmt => (
          <span key={fmt} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
            {fmt}
          </span>
        ))}
      </div>
    </div>
  );
};

// Main Component
export default function AttendanceReports() {
  const [selectedReport, setSelectedReport] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    department: 'all',
    employee: 'all',
  });

  const handleGenerateReport = (report) => {
    setSelectedReport(report);
    setShowPreview(true);
  };

  const handleDownload = (format) => {
    toast.success(`Downloading ${selectedReport.name} as ${format}...`);
    // Simulate download
    setTimeout(() => {
      toast.success(`${format} downloaded successfully`);
    }, 1500);
  };

  const handleEmail = (report) => {
    toast.success(`Email dialog would open for ${report.name}`);
  };

  const handlePrint = () => {
    window.print();
    toast.success('Print dialog opened');
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-gray-900 mb-4">Report Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
            <select
              value={filters.department}
              onChange={(e) => setFilters({ ...filters, department: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Departments</option>
              {DEPARTMENTS.map(d => (
                <option key={d.id} value={d.name}>{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
            <select
              value={filters.employee}
              onChange={(e) => setFilters({ ...filters, employee: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Employees</option>
              <option value="EMP001">Rahul Sharma (EMP001)</option>
              <option value="EMP002">Priya Patel (EMP002)</option>
              <option value="EMP003">Amit Kumar (EMP003)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Report Cards Grid */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-4">Available Reports</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {REPORT_TYPES.map((report) => (
            <ReportCard
              key={report.id}
              report={report}
              onGenerate={handleGenerateReport}
            />
          ))}
        </div>
      </div>

      {/* Recent Reports */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Recently Generated</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {[
            { name: 'Monthly Muster Roll - December 2024', date: '28 Dec 2024', format: 'PDF', size: '245 KB' },
            { name: 'Late Arrivals Report - Week 52', date: '27 Dec 2024', format: 'Excel', size: '128 KB' },
            { name: 'Attendance Summary - Q4 2024', date: '25 Dec 2024', format: 'PDF', size: '512 KB' },
          ].map((item, idx) => (
            <div key={idx} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <FileText size={18} className="text-gray-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{item.name}</p>
                  <p className="text-xs text-gray-500">{item.date} • {item.format} • {item.size}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition" title="View">
                  <Eye size={16} />
                </button>
                <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition" title="Download">
                  <Download size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Report Preview Modal */}
      {showPreview && selectedReport && (
        <ReportPreviewModal
          report={selectedReport}
          filters={filters}
          onClose={() => setShowPreview(false)}
          onDownload={handleDownload}
          onEmail={handleEmail}
          onPrint={handlePrint}
        />
      )}
    </div>
  );
}
